import type { LiquidOptions } from 'liquidjs';
import { Liquid, LiquidError } from 'liquidjs';

/** Liquid options minus the ones `createLiquidEngine` reserves. */
export type SafeLiquidOptions = Omit<
  LiquidOptions,
  'templates' | 'fs' | 'root' | 'partials' | 'layouts' | 'relativeReference'
>;

export const defaultLiquidConfig = {
  strictFilters: true,
  strictVariables: true,
};

/**
 * Build a Liquid engine. Always use this rather than `new Liquid`: template
 * bodies are authored by Provider users, and an engine left with LiquidJS's
 * defaults renders files from the working directory into its output via
 * `{% include %}`, `{% render %}` and `{% layout %}`.
 *
 * A `templates` map resolves partials from memory instead, overriding every
 * file system option. It has a null prototype because LiquidJS looks a partial
 * up with a plain property read, so an object literal would resolve `toString`
 * and every other inherited key as template source.
 */
export const createLiquidEngine = (liquidConfig: SafeLiquidOptions = {}) =>
  new Liquid({
    ...liquidConfig,
    templates: Object.create(null) as Record<string, string>,
  });

export const getFullVariablesInTemplate = async (templateHtml: string) => {
  return await createLiquidEngine(defaultLiquidConfig).fullVariables(
    templateHtml
  );
};

export const findMissingRequiredVariables = async (
  template: string,
  requiredVariables: string[]
): Promise<string[]> => {
  let presentVariables: string[];
  try {
    presentVariables = await getFullVariablesInTemplate(template);
  } catch {
    // Static analysis only fails when the template does not parse, which the
    // Liquid error collector reports separately. Treat it as "cannot tell".
    return [];
  }

  return requiredVariables.filter(
    (variable) => !presentVariables.includes(variable)
  );
};

export const assertRequiredVariablesInTemplate = async (
  template: string,
  requiredVariables: string[]
) => {
  const missingVariables = await findMissingRequiredVariables(
    template,
    requiredVariables
  );

  if (missingVariables.length > 0) {
    throw new Error(
      `Missing required variables in template: ${missingVariables.join(', ')}`
    );
  }
};

export type LiquidErrorPhase = 'parse' | 'render';

/**
 * One problem found in a Liquid template, located within the source. `parse`
 * errors are syntax problems (bad tags, unmatched blocks); `render` errors are
 * evaluation problems (unknown variables or filters). Render errors are only
 * reachable once the template parses, so a result never mixes the two phases.
 */
export type LiquidTemplateError = {
  phase: LiquidErrorPhase;
  message: string;
  line?: number;
  column?: number;
  /** Caret-annotated source excerpt pointing at the problem. */
  excerpt?: string;
};

// liquidjs exports its aggregate error (LiquidErrors) type-only from the package
// root, so we recognise it by shape rather than by class.
const isAggregateError = (error: unknown): error is { errors: unknown[] } =>
  typeof error === 'object' &&
  error !== null &&
  Array.isArray((error as { errors?: unknown }).errors);

const describeLiquidError = (
  error: unknown,
  phase: LiquidErrorPhase
): LiquidTemplateError => {
  if (!LiquidError.is(error)) {
    return {
      phase,
      message: error instanceof Error ? error.message : String(error),
    };
  }

  const [line, column] = error.token.getPosition();

  return {
    phase,
    message:
      error.originalError?.message ?? error.message ?? 'Unknown template error',
    line,
    column,
    excerpt: error.context,
  };
};

const collectFromThrown = (
  error: unknown,
  phase: LiquidErrorPhase
): LiquidTemplateError[] =>
  (isAggregateError(error) ? error.errors : [error]).map((each) =>
    describeLiquidError(each, phase)
  );

/**
 * Render a template against a context and return every problem found, rather
 * than throwing on the first. Runs in two phases: it parses first, and only
 * renders once parsing succeeds. A template with syntax errors therefore
 * returns those alone, since unknown-variable errors cannot be reached until
 * the syntax is valid. Returns an empty array when the template is valid.
 */
export const collectLiquidTemplateErrors = async (
  template: string,
  variablesMap: object,
  liquidConfig: SafeLiquidOptions = {}
): Promise<LiquidTemplateError[]> => {
  const liquid = createLiquidEngine({
    ...defaultLiquidConfig,
    ...liquidConfig,
    catchAllErrors: true,
  });

  let parsed;
  try {
    parsed = liquid.parse(template);
  } catch (error) {
    return collectFromThrown(error, 'parse');
  }

  try {
    await liquid.render(parsed, variablesMap);
  } catch (error) {
    return collectFromThrown(error, 'render');
  }

  return [];
};

/**
 * WARNING: server callers most likely want `evaluateLiquidTemplateForAccount`
 * instead — this method alone will not set the correct timezone for Liquid to
 * use. Pass a `timezoneOffset` via `liquidConfig` to control it.
 */
export const evaluateLiquidTemplate = async (
  template: string,
  variablesMap: object,
  liquidConfig: SafeLiquidOptions = {}
) => {
  try {
    const res = await createLiquidEngine({
      ...defaultLiquidConfig,
      ...liquidConfig,
    }).parseAndRender(template, variablesMap);

    return res as string;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
